import SEOMeta from "../components/SEOMeta";
import { Reveal } from "../components/Reveal";

const LegalPage = ({ title, seoTitle, description, sections, testId }) => (
  <div>
    <SEOMeta title={seoTitle} description={description} />
    <section className="pt-44 pb-24 sm:pb-32">
      <div className="max-w-3xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-gold mb-8 uppercase">Legal</p>
          <h1 className="font-display tracking-tight text-white text-4xl sm:text-5xl mb-4" data-testid={`${testId}-title`}>
            {title}
          </h1>
          <p className="font-mono text-[11px] text-white/40 mb-14">Last updated: July 2026</p>
        </Reveal>
        {sections.map((s, i) => (
          <Reveal key={s.heading} delay={0.03 * i}>
            <div className="mb-10" data-testid={`${testId}-section-${i}`}>
              <h2 className="font-display text-xl text-white mb-3">
                <span className="font-mono text-sm text-gold mr-3">{String(i + 1).padStart(2, "0")}</span>
                {s.heading}
              </h2>
              <p className="text-white/60 text-sm sm:text-base leading-relaxed pl-9">{s.body}</p>
            </div>
          </Reveal>
        ))}
        <Reveal>
          <p className="mt-14 pt-8 border-t border-white/10 text-sm text-white/50">
            For any questions regarding this page, write to us at{" "}
            <a href="mailto:support@touson.in" className="text-gold hover:text-gold-light transition-colors duration-300">
              support@touson.in
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  </div>
);

export const PrivacyPolicy = () => (
  <LegalPage
    title="Privacy Policy"
    seoTitle="Privacy Policy | Touson Ventures (OPC) Private Limited"
    description="Privacy Policy of Touson Ventures (OPC) Private Limited — how we collect, use and protect information shared with us through touson.in."
    testId="privacy"
    sections={[
      {
        heading: "Who We Are",
        body: "This website is operated by Touson Ventures (OPC) Private Limited (CIN: U47912PN2026OPC254881), a company registered in India with its registered office at OFF.204, Sapphire Chambers, S.No. 2/3/1 & 2/3/2, Riviresa, Baner Gaon, Pune, Haveli, Maharashtra, India – 411045.",
      },
      {
        heading: "Information We Collect",
        body: "When you contact us through our website, we collect the information you provide in the contact form — your name, email address, phone number (if provided), enquiry type and message. We may also collect basic technical information such as browser type and pages visited, used only to improve the website.",
      },
      {
        heading: "How We Use Your Information",
        body: "We use the information you share with us solely to respond to your enquiry, evaluate potential partnerships or opportunities, and communicate with you about your request. We do not sell, rent or share your personal information with third parties for their marketing purposes.",
      },
      {
        heading: "Data Retention & Security",
        body: "Enquiry records are retained only for as long as needed to address your request and for reasonable business record-keeping. We apply appropriate technical and organisational measures to protect the information we hold.",
      },
      {
        heading: "Third-Party Services",
        body: "We use trusted service providers to operate this website and deliver email communications. These providers process information only as needed to provide their services to us.",
      },
      {
        heading: "Your Rights",
        body: "You may request access to, correction of, or deletion of the personal information we hold about you by writing to us at support@touson.in.",
      },
      {
        heading: "Changes to This Policy",
        body: "We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.",
      },
    ]}
  />
);

export const TermsConditions = () => (
  <LegalPage
    title="Terms & Conditions"
    seoTitle="Terms & Conditions | Touson Ventures (OPC) Private Limited"
    description="Terms and conditions governing the use of touson.in, the website of Touson Ventures (OPC) Private Limited, Pune, India."
    testId="terms"
    sections={[
      {
        heading: "Acceptance of Terms",
        body: "By accessing or using this website (touson.in), you agree to be bound by these Terms & Conditions. If you do not agree, please do not use this website.",
      },
      {
        heading: "About This Website",
        body: "This website is owned and operated by Touson Ventures (OPC) Private Limited (CIN: U47912PN2026OPC254881). It provides general information about the company, its approach and its businesses, and a means to contact us.",
      },
      {
        heading: "Intellectual Property",
        body: "All content on this website — including text, design, graphics and the Touson name and branding — is the property of Touson Ventures (OPC) Private Limited and may not be reproduced, distributed or used without our prior written permission.",
      },
      {
        heading: "Use of the Website",
        body: "You agree to use this website only for lawful purposes and in a manner that does not infringe the rights of, or restrict the use of this website by, any third party. You must not attempt to gain unauthorised access to any part of the website or its systems.",
      },
      {
        heading: "No Offer or Advice",
        body: "Nothing on this website constitutes an offer, solicitation, investment advice or a recommendation of any kind. Information provided through this website does not create any business, partnership or advisory relationship.",
      },
      {
        heading: "Limitation of Liability",
        body: "While we take care to keep the information on this website accurate, it is provided on an 'as is' basis without warranties of any kind. To the fullest extent permitted by law, Touson Ventures (OPC) Private Limited shall not be liable for any loss or damage arising from the use of this website.",
      },
      {
        heading: "Governing Law",
        body: "These Terms & Conditions are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts at Pune, Maharashtra, India.",
      },
      {
        heading: "Changes to These Terms",
        body: "We may revise these Terms & Conditions at any time. Continued use of the website after changes are posted constitutes acceptance of the revised terms.",
      },
    ]}
  />
);

export const Disclaimer = () => (
  <LegalPage
    title="Disclaimer"
    seoTitle="Disclaimer | Touson Ventures (OPC) Private Limited"
    description="Disclaimer for touson.in — the website of Touson Ventures (OPC) Private Limited, Pune, Maharashtra, India."
    testId="disclaimer"
    sections={[
      {
        heading: "General Information Only",
        body: "The content on this website is provided by Touson Ventures (OPC) Private Limited for general informational purposes only. It does not constitute professional, legal, financial or investment advice, and should not be relied upon as such.",
      },
      {
        heading: "No Offer or Solicitation",
        body: "Nothing on this website constitutes an offer to sell, or a solicitation of an offer to buy, any securities, or an offer to enter into any partnership, joint venture or investment arrangement. Any such arrangement would be subject to separate, definitive agreements.",
      },
      {
        heading: "Accuracy of Information",
        body: "We strive to keep the information on this website accurate and current, but we make no representations or warranties of any kind, express or implied, about the completeness, accuracy or reliability of the information. Any reliance you place on such information is strictly at your own risk.",
      },
      {
        heading: "Forward-Looking Statements",
        body: "Statements on this website regarding future ventures, plans or opportunities are forward-looking in nature and subject to change. They do not constitute commitments or guarantees of future performance or outcomes.",
      },
      {
        heading: "External Links",
        body: "This website may contain links to external websites not operated by us. We are not responsible for the content or practices of any third-party websites.",
      },
      {
        heading: "Contact",
        body: "If you have questions about this Disclaimer, please contact us at support@touson.in or write to our registered office: OFF.204, Sapphire Chambers, S.No. 2/3/1 & 2/3/2, Riviresa, Baner Gaon, Pune, Haveli, Maharashtra, India – 411045.",
      },
    ]}
  />
);
